import React from 'react';

const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/memomizer-quick-add/hmniehlgampnhhplbdnpaakfbcdakaol';

const features = [
  {
    title: 'Right-click context menu',
    description: 'Select any word or phrase on the page and right-click to add it instantly via the context menu.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M4 6h16M4 10h16M4 14h8" />
      </svg>
    ),
  },
  {
    title: 'Floating selection icon',
    description: 'A small icon appears next to selected text — click it to add the word without leaving the page.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
      </svg>
    ),
  },
  {
    title: 'AI-personalized examples',
    description: 'Examples are generated using your interests, so every sentence feels relevant to you.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
  },
  {
    title: 'Session word counter',
    description: 'A badge on the extension icon tracks how many words you\'ve added this browsing session.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
      </svg>
    ),
  },
];

const steps = [
  { step: '1', text: 'Select a word or phrase on any web page.' },
  { step: '2', text: 'Click the floating icon or right-click → "Add to Memomizer".' },
  { step: '3', text: 'The word is saved with AI-generated, personalized examples.' },
];

const AddToChromeButton = () => (
  <a
    href={CHROME_STORE_URL}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
  >
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" />
    </svg>
    Add to Chrome — it's free
  </a>
);

const ExtensionPage = () => {
  return (
    <div className="max-w-3xl mx-auto space-y-6">

      {/* Hero */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
          </svg>
          <h1 className="text-2xl font-bold text-gray-900">Memomizer – Quick Add</h1>
        </div>
        <p className="text-gray-600 mb-6 text-lg">
          Add any word to your vocabulary list in one click — without leaving the page.
        </p>
        <AddToChromeButton />
      </div>

      {/* How it works */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">How it works</h2>
        <ol className="space-y-3">
          {steps.map(({ step, text }) => (
            <li key={step} className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                {step}
              </span>
              <p className="text-gray-700 pt-1">{text}</p>
            </li>
          ))}
        </ol>
      </div>

      {/* See it in action */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">See it in action</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((n) => (
            <img
              key={n}
              src={`/images/extension/${n}.png`}
              alt={`Extension screenshot ${n}`}
              className="rounded-lg border border-gray-200 w-full object-cover"
            />
          ))}
        </div>
      </div>

      {/* Features */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Features</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {features.map((feature) => (
            <div key={feature.title} className="flex gap-3 p-4 bg-gray-50 rounded-lg">
              <span className="text-primary-600 shrink-0 mt-0.5">{feature.icon}</span>
              <div>
                <p className="font-medium text-gray-900 mb-1">{feature.title}</p>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="bg-white shadow rounded-lg p-6 text-center">
        <p className="text-gray-600 mb-4">Ready to build your vocabulary while you browse?</p>
        <AddToChromeButton />
      </div>

    </div>
  );
};

export default ExtensionPage;
